import { useCallback } from 'react';
import useAuth from './useAuth';
import { userRoleNames, UserRole } from '../constants/defaultValues';

/**
 * What this organisation's industry calls a role.
 *
 * The signup form was only the first screen to say "Builder". Once a venue
 * operator is inside the product, the account menu, the team list, the invite
 * dialogs and every role chip go on calling them a Builder — the same
 * construction wording, in the places they see far more often than the form
 * they filled in once.
 *
 * The labels come from the API on /v1/users/me, beside the workplace noun and
 * for the same reasons: they belong to the business type, the backend already
 * needs them for emails, and one source stops the two drifting.
 *
 * Only `builder` and `subcontractor` are relabelled. They are the two roles
 * that mean "owns the workplaces" and "works for whoever does" — the pair every
 * industry has under different names. Employee, supervisor and the admin roles
 * are the same job in every industry and keep their existing names.
 *
 * The role SLUG never changes. A hospitality owner is still `builder` in the
 * database, so every permission and rule written about builders keeps applying
 * to them — which is the point of relabelling rather than re-modelling.
 */
export type RoleLabels = Partial<Record<UserRole, { name?: string; description?: string }>>;

export function useRoleLabels(): RoleLabels | null {
  const { user } = useAuth();
  const workplace = (user as { workplace?: { roleLabels?: RoleLabels } } | null)?.workplace;
  return workplace?.roleLabels ?? null;
}

/**
 * `roleName(role)` — the industry's word for a role, falling back to the
 * product's existing wording.
 *
 * Falls back rather than returning empty for every case that is not a
 * relabelled industry: Construction, an organisation with no business type, a
 * screen rendered before the user has loaded, and an industry whose labels the
 * client has not authored. A role chip reading nothing would be worse than one
 * reading the wrong industry's word.
 */
export default function useRoleName(): (role: UserRole | null | undefined) => string {
  const labels = useRoleLabels();

  return useCallback(
    (role) => {
      if (role == null) return '';
      return labels?.[role]?.name || userRoleNames[role] || String(role);
    },
    [labels]
  );
}
